import { FC, SyntheticEvent, useCallback, useMemo, useRef, useState } from 'react';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { ExpandLess as CollapseIcon, ExpandMore as ExpandIcon } from '@mui/icons-material';

import { ITableMetaProps, MetaWithCounterType } from './types';
import { ChipInPopover, ChipLabelWrapper, ChipStyled, ChipWrapper, PopoverStyled } from './styles';
import { setChipLabel, sortMetaByCount } from './helpers';

const blankClick = (e: SyntheticEvent) => {
  e.stopPropagation();
};

export const TableMeta: FC<ITableMetaProps> = (props) => {
  const { meta, requiredMeta, filterBy, maxChipWidth: maxWidth, onUpdate } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const ref = useRef<HTMLDivElement>(null);

  const filterMetaByFilterBy = useCallback(
    (newMeta: MetaWithCounterType[]) => {
      if (!filterBy?.length) {
        return newMeta;
      }
      return newMeta.filter(({ key: metaKey }) => filterBy.some(({ key: filterKey }) => filterKey === metaKey));
    },
    [filterBy]
  );

  const setChipColor = useCallback(
    (metaKey: string) => {
      if (!requiredMeta?.length) {
        return 'primary';
      }
      const isRequired = requiredMeta.some(({ key: requiredKey }) => requiredKey === metaKey);
      return isRequired ? 'secondary' : 'primary';
    },
    [requiredMeta]
  );

  const initialMeta = useMemo(() => {
    if (!filterBy?.length) {
      if (!requiredMeta?.length) {
        return meta.slice().sort(sortMetaByCount)[0];
      }
      return requiredMeta.slice().sort(sortMetaByCount)[0];
    }

    if (!requiredMeta?.length) {
      const filteredMeta = filterMetaByFilterBy(meta);
      if (filteredMeta.length > 0) {
        return filteredMeta.slice().sort(sortMetaByCount)[0];
      }
      return meta.slice().sort(sortMetaByCount)[0];
    }

    const filteredRequiredMeta = filterMetaByFilterBy(requiredMeta);
    return filteredRequiredMeta.slice().sort(sortMetaByCount)[0];
  }, [filterBy, requiredMeta, filterMetaByFilterBy, meta]);

  const handleDelete = useMemo(
    () =>
      onUpdate !== undefined
        ? (key: string) => {
            const filteredMeta = meta.filter(({ key: metaKey }) => key === metaKey);
            onUpdate(filteredMeta);
          }
        : undefined,
    [meta, onUpdate]
  );

  const initialChip = useMemo(
    () => (
      <Tooltip placement='top' title={setChipLabel(initialMeta)}>
        <ChipStyled
          label={
            <ChipLabelWrapper style={maxWidth !== undefined ? { maxWidth } : {}}>
              {setChipLabel(initialMeta)}
            </ChipLabelWrapper>
          }
          color={setChipColor(initialMeta.key)}
          clickable={!!onUpdate}
          onDelete={handleDelete}
          size='small'
          variant='outlined'
        />
      </Tooltip>
    ),
    [handleDelete, initialMeta, maxWidth, onUpdate, setChipColor]
  );

  const sortedMeta = useMemo(() => meta.slice().sort(sortMetaByCount), [meta]);

  const handleSetAnchorEl = (isOpen: boolean) => (event: SyntheticEvent) => {
    event.stopPropagation();
    if (!isOpen) {
      setAnchorEl(null);
      return;
    }
    setAnchorEl(ref.current);
  };

  return (
    <>
      <Stack ref={ref} alignItems='center' direction='row'>
        {initialChip}
        <IconButton onClick={handleSetAnchorEl(!anchorEl)} size='medium'>
          {anchorEl ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>
      </Stack>
      <PopoverStyled
        id={anchorEl ? 'table-meta-popover' : 'null'}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleSetAnchorEl(false)}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <ChipWrapper onClick={blankClick}>
          {sortedMeta.map((metaItem) => (
            <ChipInPopover
              label={setChipLabel(metaItem)}
              color={setChipColor(metaItem.key)}
              key={setChipLabel(metaItem)}
              onDelete={handleDelete}
              clickable={!!onUpdate}
              size='small'
              variant='outlined'
            />
          ))}
        </ChipWrapper>
      </PopoverStyled>
    </>
  );
};
